'use client';

import 'react-vertical-timeline-component/style.min.css';

import { Button } from '@mcdougald/ui/src';
import { useTheme } from 'next-themes';
// import { useSectionInView } from '@/lib/hooks';
// Import the icon library you're using
import {
  VerticalTimeline,
  VerticalTimelineElement,
} from 'react-vertical-timeline-component';

import {
  JOB_HISTORY_RENDER_DATA,
  type RenderResumeJob,
} from '@/constants/data/job-history';
import { HOME_SECTION_IDS } from '@/constants/routing';
import { useSectionInView } from '@/hooks/use-section-in-view';
import { useJobHistoryExpandedState } from '@/store/resume';

import { cn } from '../../../libraries/tailwind/cn';
import SectionHeading, { AuroraSectionHeading } from '../section-heading';

const getBackground = (isLightTheme: boolean): string => {
  return isLightTheme ? '#f3f4f6' : 'rgba(255, 255, 255, 0.05)';
};

const WorkTimelineElement = (props: { item: RenderResumeJob }) => {
  const { theme } = useTheme();

  const { state, toggleJob } = useJobHistoryExpandedState();

  // @ts-expect-error - string index works
  const isExpanded = state[props.item.id] ?? false;

  const { title, date, company, Icon, tasks, location } = props.item;

  return (
    <VerticalTimelineElement
      className="vertical-timeline-element--work"
      contentStyle={{
        background: getBackground(theme === 'light'),
        boxShadow: 'none',
        border: `1px solid ${theme === 'light' ? 'rgba(0, 0, 0, 0.15)' : 'rgba(255, 255, 255, 0.5)'}`,
        textAlign: 'left',
        padding: '1.3rem 2rem',
      }}
      dateClassName={'mx-2'}
      contentArrowStyle={{
        borderRight: `7px solid ${theme === 'light' ? 'rgba(0, 0, 0, 0.15)' : 'rgba(255, 255, 255, 0.5)'}`,
      }}
      date={date}
      iconStyle={{
        background: theme === 'light' ? 'white' : 'rgb(51 54 62)',
        fontSize: '1.5rem',
      }}
      icon={Icon ? <Icon /> : undefined}
      visible
    >
      <div className={'flex justify-between'}>
        <h3 className="vertical-timeline-element-title font-bold">{title}</h3>
        <h3 className="vertical-timeline-element-title font-bold underline">
          {company}
        </h3>
      </div>
      <h4 className="vertical-timeline-element-subtitle">{location}</h4>
      <Button
        className={'flex ml-auto'}
        content={isExpanded ? 'Hide' : 'Expand'}
        // @ts-expect-error - typed correctly for string
        onClick={() => toggleJob(props.item.id)}
      >
        {isExpanded ? 'Hide' : 'See Details'}
      </Button>
      {isExpanded ? (
        <p>
          {tasks.map((task, i) => {
            return (
              <p
                key={`${task}-${String(i)}`}
                className="mb-2 flex gap-2 !text-sm"
              >
                <span className={'w-[20px]'}>―</span>
                {task}
              </p>
            );
          })}
        </p>
      ) : null}
    </VerticalTimelineElement>
  );
};

/**
 * Ripped from CodeSandbox
 *
 * - https://codesandbox.io/p/devbox/vigilant-bouman-dmktm3?file=%2Flib%2Fdata.ts%3A2%2C1-3%2C77
 *
 */
export default function WorkExperience(props: {
  jobHistoryCount?: number;
  className?: string;
}) {
  const { ref } = useSectionInView('Experience');
  const { theme } = useTheme();

  const { jobHistoryCount, className } = props;

  const jobs = jobHistoryCount
    ? JOB_HISTORY_RENDER_DATA.slice(0, jobHistoryCount)
    : JOB_HISTORY_RENDER_DATA;

  // Function to determine background based on the theme

  const lineColor = theme === 'light' ? '#e5e7eb' : 'rgba(229, 231, 235, 0.2)'; // bg-gray-200 color in hex

  return (
    <section
      id={HOME_SECTION_IDS.experience}
      ref={ref}
      className={cn('scroll-mt-28 mb-28 sm:mb-40 flex flex-col', className)}
    >
      <AuroraSectionHeading className={'text-center mx-auto font-bold mb-8'}>
        Work Experience
      </AuroraSectionHeading>
      <VerticalTimeline lineColor={lineColor}>
        {jobs.map((item, index) => {
          return (
            <WorkTimelineElement
              key={`vertical-history-item-${String(index)}`}
              item={item}
            />
          );
        })}
      </VerticalTimeline>
    </section>
  );
}
